import { RejectedBannerIcon } from '@/assets/icons/info-icons/rejectedBanner';
import { Banner } from '@/components/basicComponents/banner';
import { FormFooter } from '@/components/basicComponents/formFooter';
import Input from '@/components/basicComponents/input';
import Select from '@/components/basicComponents/select';
import { createSimpleSelectOption } from '@/components/basicComponents/select/utils';
import Header from '@/components/header';
import { GoBackButton } from '@/components/header/goBackButton';
import { PageTitle } from '@/components/header/pageTitle';
import { TwoColumnWrapper } from '@/components/transactions-page/generateSignaturePacket-page/templateComponents/styles';
import { useErrorMessages } from '@/hooks/i18n';
import { useResponsive } from '@/hooks/use-responsive';
import { MainWrap, PaddingWrap } from '@/styles/common';
import { themeVar } from '@/styles/theme';
import { useMutation } from '@apollo/client';
import { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import styled from 'styled-components';
import { FormFieldsWrapper } from '..';
import { CREATE_INVESTMENT_VEHICLE_MUTATION, UPDATE_INVESTMENT_VEHICLE_MUTATION } from '../../queries';
import { InvestmentVehicleDto } from '../types';

interface IForm {
  vehicleCode: string;
  vehicleName: string;
  taxStatus: string;
}

const defaultFormValues: IForm = {
  vehicleCode: '',
  vehicleName: '',
  taxStatus: ''
};

const taxStatusOptions = [createSimpleSelectOption('Taxable'), createSimpleSelectOption('Exempt')];

interface EnterVehicleInfoModalPageProps {
  investmentId: string;
  currentVehicle: InvestmentVehicleDto | null;
  investmentVehiclesRefetch: () => void;
  handleCloseModalPage: () => void;
}

export const EnterVehicleInfoModalPage = ({
  investmentId,
  currentVehicle,
  investmentVehiclesRefetch,
  handleCloseModalPage
}: EnterVehicleInfoModalPageProps) => {
  const { isMobile } = useResponsive();
  const { default: defaultErrorMessage } = useErrorMessages();
  const {
    handleSubmit,
    control,
    reset,
    formState: { dirtyFields }
  } = useForm<IForm>({ defaultValues: defaultFormValues });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const [createInvestmentVehicle] = useMutation(CREATE_INVESTMENT_VEHICLE_MUTATION);
  const [updateInvestmentVehicle] = useMutation(UPDATE_INVESTMENT_VEHICLE_MUTATION);

  useEffect(() => {
    if (!currentVehicle) return;

    reset({
      vehicleCode: currentVehicle?.id,
      vehicleName: currentVehicle?.name || '',
      taxStatus: typeof currentVehicle?.taxExempt === 'boolean' ? (currentVehicle?.taxExempt ? 'Exempt' : 'Taxable') : ''
    });
  }, [currentVehicle]);

  const onCreateInvestmentVehicle = (data: IForm) => {
    setErrorMessage(null);
    if (!investmentId) {
      setErrorMessage(defaultErrorMessage);
      return;
    }
    setIsSubmitting(true);
    const { vehicleCode, vehicleName, taxStatus } = data;

    createInvestmentVehicle({
      variables: {
        data: {
          investmentId,
          seriesCode: vehicleCode,
          ...(dirtyFields?.vehicleName && { seriesName: vehicleName }),
          ...(dirtyFields?.taxStatus && { taxExempt: taxStatus === 'Exempt' })
        }
      },
      onCompleted: () => {
        investmentVehiclesRefetch();
        handleCloseModalPage();
        setIsSubmitting(false);
      },
      onError: (error) => {
        console.error(error);
        setIsSubmitting(false);
        setErrorMessage(defaultErrorMessage);
      }
    });
  };

  const onUpdateInvestmentVehicle = (data: IForm) => {
    setErrorMessage(null);
    setIsSubmitting(true);
    const { vehicleCode, vehicleName, taxStatus } = data;

    updateInvestmentVehicle({
      variables: {
        data: {
          seriesCode: currentVehicle?.id,
          ...(dirtyFields?.vehicleCode && { newSeriesCode: vehicleCode }),
          ...(dirtyFields?.vehicleName && { seriesName: vehicleName }),
          ...(dirtyFields?.taxStatus && { taxExempt: taxStatus === 'Exempt' })
        }
      },
      onCompleted: () => {
        investmentVehiclesRefetch();
        handleCloseModalPage();
        setIsSubmitting(false);
      },
      onError: (error) => {
        console.error(error);
        setIsSubmitting(false);
        setErrorMessage(defaultErrorMessage);
      }
    });
  };

  const onSubmit = (data: IForm) => {
    if (currentVehicle) {
      onUpdateInvestmentVehicle(data);
      return;
    }
    onCreateInvestmentVehicle(data);
  };

  return (
    <MainWrap>
      <Header modalControl={<GoBackButton handleClose={handleCloseModalPage} backToTitle={''} />} />
      <PageTitle title="Enter Vehicle Info" />
      {errorMessage && (
        <Banner
          icon={<RejectedBannerIcon fill={themeVar('context.light')} />}
          title="Error"
          description={errorMessage}
          bgColor={themeVar('context.error')}
        />
      )}
      <PaddingWrap>
        <Form id="vehicleInfoForm" onSubmit={handleSubmit(onSubmit, () => window.scrollTo(0, 0))}>
          <FormFieldsWrapper>
            <TwoColumnWrapper isMobile={isMobile}>
              <Controller
                name="vehicleCode"
                control={control}
                rules={{
                  required: {
                    value: true,
                    message: 'Vehicle Code is required'
                  }
                }}
                render={({ field: { onChange, value }, fieldState: { error } }) => (
                  <Input
                    value={value || ''}
                    onChange={(e) => {
                      onChange(e.target.value);
                    }}
                    label="Vehicle Code"
                    errorText={error?.message}
                    size="md"
                    styleType={currentVehicle?.hasAllocations || currentVehicle?.hasCommitments ? 'disabled' : 'default'}
                    isRequiredField={!currentVehicle}
                  />
                )}
              />
            </TwoColumnWrapper>
            <TwoColumnWrapper isMobile={isMobile}>
              <Controller
                name="vehicleName"
                control={control}
                render={({ field: { onChange, value }, fieldState: { error } }) => (
                  <Input
                    value={value || ''}
                    onChange={(e) => {
                      onChange(e.target.value);
                    }}
                    label="Vehicle Name"
                    errorText={error?.message}
                    size="md"
                  />
                )}
              />
              <Controller
                name="taxStatus"
                control={control}
                render={({ field: { onChange, value }, fieldState: { error } }) => (
                  <Select
                    value={value || ''}
                    setValue={onChange}
                    options={taxStatusOptions}
                    label="Tax Status"
                    errorText={error?.message}
                    size="md"
                  />
                )}
              />
            </TwoColumnWrapper>
          </FormFieldsWrapper>
          <FormFooter onCancel={handleCloseModalPage} disableSaveButton={isSubmitting} isLoading={isSubmitting} />
        </Form>
      </PaddingWrap>
    </MainWrap>
  );
};

const Form = styled.form`
  padding: 50px 0;
  width: 100%;
  background: ${themeVar('layer.base')};
`;
